import * as _ from 'lodash';

import {
    Component,
    OnDestroy,
    OnInit,
    AfterViewInit,
    ElementRef,
    ViewChild,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import {
    AbstractControlOptions,
    FormBuilder,
    FormGroup,
    Validators,
    ReactiveFormsModule,
} from '@angular/forms';
import moment from 'moment';
import { dateValidator, timeValidator } from '../history/customValidators';
import { marker as _translate } from '@biesbjerg/ngx-translate-extract-marker';
import { firstValueFrom, Subject, finalize, takeUntil } from 'rxjs';
import {
    Blade,
    IBladeComponent,
} from 'src/app/modules/blades/models/blade-component';
import { SignInService } from 'src/app/modules/core/services';
import { MapStateService } from 'src/app/modules/map/map-state.service';
import { DialogService } from 'src/app/modules/shared/services/dialog.service';
import { TranslateService } from '../../modules/core/services';
import { User } from 'src/app/modules/shared/models';
import { DamageRiskStatus } from 'src/app/modules/shared/models/damage-risk-status';
import { ConfirmationDialogData } from 'src/app/modules/shared/dialogs';
import { TaService } from 'src/app/modules/core/services/ta.service';
import { NotificationService } from 'src/app/modules/shared/services/notification.service';
import { DamageRisk } from 'src/app/modules/shared/models/damage-risk';
import { AppStateService } from 'src/app/modules/core/services/app-state.service';
import { Garage } from 'src/app/modules/shared/models/garage';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDividerModule } from '@angular/material/divider';
import { BladeHeaderComponent } from '../../modules/blades/components/blade-header/blade-header.component';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-register-damage-risk',
    styleUrls: ['./register-damage-risk.component.scss'],
    templateUrl: './register-damage-risk.component.html',
    imports: [
        BladeHeaderComponent,
        ReactiveFormsModule,
        MatDividerModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        MatButtonModule,
        DatePipe,
        TranslateModule,
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
@Blade({
    name: 'RegisterDamageRiskComponent',
})
export class RegisterDamageRiskComponent
    implements OnInit, AfterViewInit, OnDestroy, IBladeComponent
{
    private _appStateService = inject(AppStateService);
    formBuilder = inject(FormBuilder);
    private _mapstateService = inject(MapStateService);
    private _signInService = inject(SignInService);
    private _dialogService = inject(DialogService);
    private _translateService = inject(TranslateService);
    private _notificationService = inject(NotificationService);
    private _taService = inject(TaService);
    private _router = inject(Router);

    @ViewChild('observedByInput') observedByInput: ElementRef;

    public garages: Garage[] = [];
    public trafficAreaData: { id: number; name: string }[] = [];
    public bladeIndex: number;
    public title: string;

    public isProcessing = false;
    public user: User;

    public registerDamageRiskObject: FormGroup;
    public latitude: any;
    public longitude: any;

    private readonly _defaultDatePickerDate: string =
        moment().format('YYYY-MM-DD');
    private _ngUnsubscribe$ = new Subject<void>();
    private _formHasUnsavedChanges: boolean;
    private _tempLatitude: any;
    private _tempLongitude: any;

    constructor() {}

    // oerrides behaviour of [canDeactivate] guards using dialog service.
    public async canDeactivate(): Promise<boolean> {
        if (this._formHasUnsavedChanges) {
            const modalData = new ConfirmationDialogData(
                this._translateService.instant('Warning'),
                this._translateService.instant(
                    'If you continue, you will lose your changes',
                ),
                this._translateService.instant('Continue'),
                this._translateService.instant('Cancel'),
            );

            return (
                (await firstValueFrom(
                    this._dialogService.showConfirmation(modalData),
                )) !== undefined
            );
        }

        return true;
    }

    public reset(event) {
        event.preventDefault();
        this._resetForm();
    }

    onSubmit() {
        this._formHasUnsavedChanges = false;

        if (this.registerDamageRiskObject.valid && !this.isProcessing) {
            this.isProcessing = true;
            const formData: DamageRisk = this._mapToDamageRisk();

            this._taService
                .registerDamageRisk(formData)
                .pipe(
                    finalize(() => {
                        this.isProcessing = false;
                    }),
                )
                .subscribe({
                    next: () => {
                        this._formHasUnsavedChanges = false;
                        this._notificationService.openText(
                            this._translateService.instant(
                                'New damage risk registered',
                            ),
                        );
                        this._router.navigate(['/']);
                    },
                    error: (error) => {
                        this._formHasUnsavedChanges = true;
                        console.error('Failed to register damage risk', error);
                        this._notificationService.openError(
                            this._translateService.instant(
                                'Failed to register damage risk',
                            ),
                        );
                    },
                });
        }
    }

    ngOnInit() {
        this._appStateService
            .getGaragesState()
            .pipe(takeUntil(this._ngUnsubscribe$))
            .subscribe((garages: Garage[]) => {
                this.garages = garages;

                if (this.garages) {
                    this.trafficAreaData = this.garages.map((garage) => ({
                        id: garage.trafficAreaId,
                        name: garage.trafficAreaName,
                    }));
                    this.trafficAreaData = this.trafficAreaData.filter(
                        (value, index, self) =>
                            self.findIndex((v) => v.id === value.id) === index,
                    );
                    this.trafficAreaData.sort((a, b) =>
                        a.name.localeCompare(b.name),
                    );
                }
            });

        this._formHasUnsavedChanges = true;
        this.user = this._signInService.user;

        this._signInService.user$
            .pipe(takeUntil(this._ngUnsubscribe$))
            .subscribe((user) => {
                this.user = user;
                if (this.registerDamageRiskObject) {
                    this.registerDamageRiskObject.patchValue({
                        createdBy: this.user?.userid,
                    });
                }
            });

        // listens to user rightclick in the map to place
        this._mapstateService
            .getMapPropertyState('rightClicked')
            .subscribe(
                (clickedCoordinate: {
                    lng: number;
                    lat: number;
                    x: number;
                    y: number;
                }) => {
                    if (!clickedCoordinate) return;
                    this.latitude = clickedCoordinate.lat.toFixed(5);
                    this.longitude = clickedCoordinate.lng.toFixed(5);
                },
            );
        this._tempLatitude = this.latitude;
        this._tempLongitude = this.longitude;
        this.registerDamageRiskObject = this._initForm();
    }

    ngAfterViewInit() {
        setTimeout(() => this.observedByInput?.nativeElement.focus());
    }

    ngOnDestroy() {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }

    onTrafficAreaChange(event: MatSelectChange) {
        const selectedId = event.value;
        const selectedArea = this.trafficAreaData.find(
            (area) => area.id === selectedId,
        );

        if (selectedArea) {
            this.registerDamageRiskObject.patchValue({
                trafficAreaName: selectedArea.name,
            });
        }
    }

    private _resetForm() {
        this.registerDamageRiskObject.markAsPristine();
        this.registerDamageRiskObject.markAsUntouched();
        this.registerDamageRiskObject.updateValueAndValidity();
        this.registerDamageRiskObject.reset();

        this.registerDamageRiskObject.patchValue({
            createdBy: this.user?.userid,
            createdDate: this._defaultDatePickerDate,
            observedDate: this._defaultDatePickerDate,
            observedTime: this._getCurrentTime(),
            status: DamageRiskStatus.Active,
            latitude: this._tempLatitude,
            longitude: this._tempLongitude,
        });
        setTimeout(() => this.observedByInput?.nativeElement.focus());
    }

    private _initForm(): FormGroup {
        return this.formBuilder.group(
            {
                latitude: [this.latitude],
                longitude: [this.longitude],
                createdBy: [this.user?.userid],
                createdDate: [this._defaultDatePickerDate, [dateValidator]],
                observedBy: [
                    '',
                    [Validators.required, Validators.maxLength(50)],
                ],
                observedDate: [
                    this._defaultDatePickerDate,
                    [Validators.required],
                ],
                observedTime: [
                    this._getCurrentTime(),
                    [Validators.required, timeValidator],
                ],
                address: ['', [Validators.maxLength(100)]],
                city: ['', [Validators.maxLength(50)]],
                route: ['', Validators.maxLength(20)],
                description: [
                    '',
                    [Validators.required, Validators.maxLength(500)],
                ],
                status: [DamageRiskStatus.Active],
                trafficAreaId: ['', Validators.required],
                trafficAreaName: ['', Validators.maxLength(100)],
            },
            {
                validators: [],
            } as AbstractControlOptions,
        );
    }

    private _getCurrentTime(): string {
        return moment().format('HH:mm');
    }

    private _mapToDamageRisk(): DamageRisk {
        return {
            latitude: this.latitude,
            longitude: this.longitude,
            createdBy: this.user?.userid ? this.user?.userid : '',
            observedBy: this._getFormControlValue('observedBy'),
            observed: this._observedDateTime(),
            address: this._getFormControlValue('address'),
            city: this._getFormControlValue('city'),
            route: this._getFormControlValue('route'),
            description: this._getFormControlValue('description'),
            trafficAreaId: this._getFormControlValue('trafficAreaId'),
            trafficAreaName: this._getFormControlValue('trafficAreaName'),
        };
    }

    private _observedDateTime(): Date {
        const observedDate = new Date(
            this._getFormControlValue('observedDate'),
        );
        const [h, m] = this._getFormControlValue('observedTime').split(':');
        observedDate.setHours(Number(h), Number(m));
        return observedDate;
    }

    private _getFormControlValue(controlName: string): any {
        return this.registerDamageRiskObject.get(controlName).value;
    }
}
